import {
  gql,
  useApolloClient,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { FeatureFlagTypes } from '@generated/graphql';
import type { IncompleteFormAgreement } from '@interfaces/formAgreements';
import { LanguageCodes } from '@onevesthq/ov-enums';
import type { SegmentValue } from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { DepositTransferContext } from '@ovComponents/4-module/depositWorkflow/depositTransferContext';
import { WorkflowCompletionContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionDialogContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { fetchAuthorizedIndividual } from '@utils/fetchAuthorizedIndividual';
import { uploadWithRetry } from '@utils/fileUploader';
import { resolveFileDocumentObject } from '@utils/resolveFileDocumentObject';
import { safeWindowOpen } from '@utils/urlSanitization';
import { usePageState } from '@utils/usePageState';
import hash_sum from 'hash-sum';
import { useToast } from 'hephaestus/web';
import { isNil } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { usePermission } from '../../../../../hooks/usePermission';
import {
  DocumentsVisual,
  type SignableDocumentState,
  type UploadableDocumentState,
} from './documents.visual';

const FETCH_USER = gql`
  query fetchUserIncompleteFormAgreements($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        physicalAddress {
          jurisdiction
        }
        allIncompleteFormAgreements {
          id
          account { id }
          subAccount { id }
          goal { id }
          user { id }
          scheduledTransfer { id }
          clientGroup { id }
          translatedDisplayName { ${TRANSLATED_STRING_FIELDS} }
          type
          templateUrl
          minVersion
          digitalSignatureEnabled
          isPrefilledPdfEnabled
        }
      }
    }
  }
`;

const FETCH_HOUSEHOLD = gql`
  query fetchHouseholdIncompleteFormAgreements($clientGroupId: SafeString!) {
    fetchClientGroup(input: { clientGroupId: $clientGroupId }) {
      clientGroup {
        primaryUser {
          id: stringId
          physicalAddress {
            jurisdiction
          }
          allIncompleteFormAgreements {
            id
            account { id }
            subAccount { id }
            goal { id }
            user { id }
            scheduledTransfer { id }
            clientGroup { id }
            translatedDisplayName { ${TRANSLATED_STRING_FIELDS} }
            type
            templateUrl
            minVersion
            digitalSignatureEnabled
            isPrefilledPdfEnabled
          }
        }
      }
    }
  }
`;

const SIGN_FORM_AGREEMENT = gql`
  mutation signFormAgreements($input: SignFormAgreementsInput!) {
    signFormAgreements(input: $input) {
      formAgreements {
        id
      }
    }
  }
`;

const PREVIEW_AGREEMENT = gql`
  query previewFormAgreement($input: PreviewFormAgreementInput!) {
    previewFormAgreement(input: $input) {
      html
    }
  }
`;

export const FETCH_FILE_UPLOAD_URL = gql`
  query fetchFileUploadUrl($input: FetchFileUploadUrlInput!) {
    fetchFileUploadUrl(input: $input) {
      temporarySignedURL
      timestamp
    }
  }
`;

export const CREATE_FILE_DOCUMENT = gql`
  mutation createFileDocument($input: CreateFileDocumentInput!) {
    createFileDocument(input: $input) {
      fileDocument {
        id
        name
        fileName
        s3Key
        type
      }
    }
  }
`;

const FETCH_PRE_FILLED_PDF_URL = gql`
  query previewFilledPdf($input: PreviewFilledPdfInput!) {
    previewFilledPdf(input: $input) {
      url
    }
  }
`;

const relatedFilterAttribute = (document: IncompleteFormAgreement) => {
  if (document.account?.id) {
    return { accountId: document.account?.id };
  }
  if (document.subAccount?.id) {
    return { subAccountId: document.subAccount?.id };
  }
  if (document.user?.id) {
    return { userId: document.user?.id };
  }
  if (document.goal?.id) {
    return { goalId: document.goal?.id };
  }
  return { scheduledTransferId: document.scheduledTransfer?.id };
};

export const Documents = ({
  options,
  objectId,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
  objectType = 'USER',
}: {
  options: any;
  objectId: string;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
  objectType?: string;
}) => {
  const { language } = useLocalization();
  const { enqueueToast } = useToast();
  const canManuallyUploadFormAgreement = usePermission(
    'write:manually_upload_form_agreement',
  );
  const qcJurisdictionAgreementsEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.UserQcJurisdictionAgreements,
  );

  const { sendAnalytic } = useContext(AnalyticsContext);
  const [signableDocuments, setSignableDocuments] = useState<
    SignableDocumentState[]
  >([]);
  const [uploadableDocuments, setUploadableDocuments] = useState<
    UploadableDocumentState[]
  >([]);
  const [signableDocumentIndex, setSignableDocumentIndex] = useState<
    number | undefined
  >();
  const [uploadableDocumentIndex, setUploadableDocumentIndex] = useState<
    number | undefined
  >();
  const [html, setHtml] = useState<any>('');
  const { userContext, activeEntity } = useContext(UserContext);
  const { scrollWorkflowDialog } = useContext(WorkflowCompletionContext);
  const graphqlClient = useApolloClient();
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [paramsUserId] = usePageState('', 'userId');
  const { userId: uId } = useParams<{
    id: string;
    objectId?: string;
    userId?: string;
  }>();

  const [fetchFileUploadUrl, { loading: uploadUrlLoading }] = useLazyQuery(
    FETCH_FILE_UPLOAD_URL,
    { fetchPolicy: 'no-cache' },
  );
  const [uploadPending, setUploadPending] = useState(false);
  const [createFileDocument, { loading: createFilePending }] =
    useMutation(CREATE_FILE_DOCUMENT);
  const [fetchPreFilledPdfURL, { loading: downloadUrlLoading }] = useLazyQuery(
    FETCH_PRE_FILLED_PDF_URL,
    { fetchPolicy: 'no-cache' },
  );

  const { transferData } = useContext(DepositTransferContext);

  /*
   * Language action and user jurisdiction are part of the ticket https://onewealth.atlassian.net/browse/OV-16634
   * logic implemented for the * Bill C96 * documents should be presented in french first, for user's Jurisdiction CA_QC
   * no impact for other jurisdictions.
   */
  const [languageAction, setLanguageAction] = useState<SegmentValue>(language);
  const [userJurisdiction, setUserJurisdiction] = useState<
    string | undefined
  >();

  if (transferData?.skipDocuments) {
    onNext();
  }

  const getPreFilledPdfUrl = async (
    doc: IncompleteFormAgreement,
    generatePrefilledPdf?: boolean,
  ) => {
    const result = await fetchPreFilledPdfURL({
      variables: {
        input: {
          type: doc.type,
          accountId: doc.account?.id ?? undefined,
          subAccountId: doc.subAccount?.id ?? undefined,
          userId: doc.user?.id ?? undefined,
          goalId: doc.goal?.id ?? undefined,
          scheduledTransferId: doc.scheduledTransfer?.id ?? undefined,
          clientGroupId: doc?.clientGroup?.id ?? undefined,
          ...(generatePrefilledPdf !== undefined && { generatePrefilledPdf }),
        },
      },
    });
    const url = result?.data?.previewFilledPdf?.url;
    return url;
  };

  const downloadPreFilledPdf = async (
    doc: IncompleteFormAgreement,
    generatePrefilledPdf?: boolean,
  ) => {
    const url = await getPreFilledPdfUrl(doc, generatePrefilledPdf);
    if (url) {
      // we need setTimeout to open the url in Safari
      setTimeout(() =>
        safeWindowOpen(url, {
          target: '_blank',
          onError: (errorMsg) => {
            enqueueToast(errorMsg, { variant: 'error' });
          },
        }),
      );
    }
  };

  const { data: agreementsData, loading: incompleteAgreementsLoading } =
    useQuery(objectType === 'HOUSEHOLD' ? FETCH_HOUSEHOLD : FETCH_USER, {
      variables: {
        ...(objectType === 'HOUSEHOLD'
          ? { clientGroupId: objectId }
          : { userId: uId || activeEntity?.id || paramsUserId }),
      },
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        const user =
          objectType === 'HOUSEHOLD'
            ? data.fetchClientGroup.clientGroup.primaryUser
            : data.fetchUser.user;
        if (user?.allIncompleteFormAgreements) {
          setSignableDocuments(
            user.allIncompleteFormAgreements
              .filter((x: IncompleteFormAgreement) => x.digitalSignatureEnabled)
              .map(
                (x: IncompleteFormAgreement): SignableDocumentState => ({
                  signed: false,
                  isPrefilledPdfEnabled: x.isPrefilledPdfEnabled,
                  ...x,
                  id: hash_sum(x), // need unique ID across more incompleteFormAgreements of the same type
                }),
              ),
          );

          setUploadableDocuments(
            user.allIncompleteFormAgreements
              .filter(
                (x: IncompleteFormAgreement) => !x.digitalSignatureEnabled,
              )
              .map(
                (x: IncompleteFormAgreement): UploadableDocumentState => ({
                  uploaded: false,
                  isPrefilledPdfEnabled: x.isPrefilledPdfEnabled,
                  ...x,
                }),
              ),
          );
        }
        if (user.physicalAddress !== null && qcJurisdictionAgreementsEnabled) {
          setUserJurisdiction(user.physicalAddress.jurisdiction);
          setLanguageAction(
            user.physicalAddress.jurisdiction === 'CA_QC'
              ? LanguageCodes.FR
              : language,
          );
        }
      },
    });

  const [preview, { loading: previewLoading }] = useLazyQuery(
    PREVIEW_AGREEMENT,
    {
      onCompleted: (d: any) => {
        setHtml(d.previewFormAgreement.html);
      },
    },
  );

  const [signFormAgreement, { loading: signFormAgreementPending }] =
    useMutation(SIGN_FORM_AGREEMENT);

  const currentUserId =
    objectType === 'HOUSEHOLD'
      ? agreementsData?.fetchClientGroup?.clientGroup?.primaryUser?.id
      : userId;

  const signAll = async () => {
    const unsignedDocuments = signableDocuments.filter((y) => !y.signed);

    await Promise.all(unsignedDocuments.map(doSignDocument));
  };

  const doSignDocument = async (doc: SignableDocumentState) => {
    let responsibleUserId = null;
    if (objectId) {
      responsibleUserId = await fetchAuthorizedIndividual({
        userId: currentUserId,
        apolloClient: graphqlClient,
        userContext,
      });
    }
    const result = await signFormAgreement({
      variables: {
        input: {
          ...relatedFilterAttribute(doc),
          ...(languageAction && {
            language: languageAction.toString().toUpperCase(),
          }),
          formAgreements: [
            {
              type: doc.type,
              version: doc.minVersion,
            },
          ],
          userId: currentUserId,
          ...(objectType === 'HOUSEHOLD' ? { clientGroupId: objectId } : {}),
          ...(responsibleUserId ? { signedByUserId: responsibleUserId } : {}),
        },
      },
    });
    if (!result?.errors) {
      setSignableDocuments((prevSignableDocuments) =>
        prevSignableDocuments.map((x) =>
          x.id === doc.id ? { ...x, signed: true } : x,
        ),
      );
    }
  };

  const downloadTemplatePDF = (
    type: 'signable' | 'uploadable',
    generatePrefilledPdf?: boolean,
  ) => {
    if (type === 'uploadable' && !isNil(uploadableDocumentIndex)) {
      downloadPreFilledPdf(
        uploadableDocuments[uploadableDocumentIndex],
        generatePrefilledPdf,
      );
    } else if (type === 'signable' && !isNil(signableDocumentIndex)) {
      downloadPreFilledPdf(
        signableDocuments[signableDocumentIndex],
        generatePrefilledPdf,
      );
    }
  };

  /**
   * Shared upload logic for form documents.
   * Performs a 3-step "handshake" to securely upload files:
   *   1. Fetch a temporary signed S3 URL from backend
   *   2. PUT the file directly to S3
   *   3. Create a FileDocument record in backend
   *
   * @returns The file document ID if successful, null otherwise
   */
  const uploadFormDocument = async (
    file: File,
    name: string,
    document: IncompleteFormAgreement,
    onError: () => void,
  ): Promise<string | null> => {
    const {
      objectType: associatedToObjectType,
      objectId: associatedToObjectID,
    } = resolveFileDocumentObject(document, currentUserId);

    const createFileInput: any = {
      objectType: associatedToObjectType,
      objectId: associatedToObjectID,
      fileName: file.name,
      type: 'FORM',
    };

    /* (1) fetch the S3 upload URL from backend */
    const queryResult = await fetchFileUploadUrl({
      variables: {
        input: {
          ...createFileInput,
          userId: currentUserId,
          generateUniqueFileName: true,
        },
      },
    });
    const uploadUrl = queryResult?.data?.fetchFileUploadUrl.temporarySignedURL;
    const timestamp = queryResult?.data?.fetchFileUploadUrl.timestamp;

    if (!uploadUrl || queryResult?.error) {
      enqueueToast(
        `Upload failed, step 1 of 3 (FETCH_FILE_UPLOAD_URL) ${queryResult?.error}`,
        { variant: 'error' },
      );
      onError();
      return null;
    }

    /* (2) do the upload */
    try {
      setUploadPending(true);
      const uploaded: Response = await uploadWithRetry(uploadUrl, file, 'FORM');
      if (!uploaded.ok) {
        throw new Error(`${uploaded.status} ${uploaded.statusText}`);
      }
    } catch (e: any) {
      enqueueToast(`Upload failed, step 2 of 3 (HTTP PUT) ${e}`, {
        variant: 'error',
      });
      onError();
      return null;
    } finally {
      setUploadPending(false);
    }

    /* (3) create the fileDocument within backend */
    createFileInput.name = name;
    createFileInput.mediaType = file.type;
    createFileInput.permissionType = 'ORGANIZATIONAL';
    createFileInput.sourceType = document.type;
    createFileInput.timestamp = timestamp;

    try {
      const result = await createFileDocument({
        variables: { input: createFileInput },
      });

      const fileDocumentId = result?.data?.createFileDocument?.fileDocument?.id;

      enqueueToast(`Document uploaded successfully: ${file.name}`, {
        variant: 'success',
      });

      return fileDocumentId || null;
    } catch (error) {
      enqueueToast(
        `Upload failed, step 3 of 3 (CREATE_FILE_DOCUMENT) ${error}`,
        { variant: 'error' },
      );
      onError();
      return null;
    }
  };

  const doTheUpload = async (file: File, name: string) => {
    if (uploadableDocumentIndex === undefined) return;
    const documentIndex = uploadableDocumentIndex;
    const document = uploadableDocuments[documentIndex];

    setUploadableDocumentIndex(undefined);
    setUploadableDocuments(
      uploadableDocuments.map((x: UploadableDocumentState, i: number) =>
        i === documentIndex ? { ...x, pending: true } : x,
      ),
    );

    const fileDocumentId = await uploadFormDocument(
      file,
      name,
      document,
      () => {
        setUploadableDocuments(
          uploadableDocuments.map((x: UploadableDocumentState, i: number) =>
            i === documentIndex ? { ...x, pending: false } : x,
          ),
        );
      },
    );

    if (fileDocumentId) {
      setUploadableDocuments(
        uploadableDocuments.map((x: UploadableDocumentState, i: number) =>
          i === documentIndex ? { ...x, pending: false, uploaded: true } : x,
        ),
      );
    }
  };

  const doTheUploadForSignable = async (file: File, name: string) => {
    if (signableDocumentIndex === undefined) return;
    const documentIndex = signableDocumentIndex;
    const document = signableDocuments[documentIndex];

    setSignableDocumentIndex(undefined);
    setSignableDocuments(
      signableDocuments.map((x: SignableDocumentState, i: number) =>
        i === documentIndex ? { ...x, pending: true } : x,
      ),
    );

    const fileDocumentId = await uploadFormDocument(
      file,
      name,
      document,
      () => {
        setSignableDocuments(
          signableDocuments.map((x: SignableDocumentState, i: number) =>
            i === documentIndex ? { ...x, pending: false } : x,
          ),
        );
      },
    );

    if (fileDocumentId) {
      setSignableDocuments(
        signableDocuments.map((x: SignableDocumentState, i: number) =>
          i === documentIndex ? { ...x, pending: false, signed: true } : x,
        ),
      );

      sendAnalytic('workflows_documents_upload_signed_document_complete', {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      });
    }
  };

  useEffect(() => {
    if (signableDocumentIndex !== undefined) {
      const doc = signableDocuments[signableDocumentIndex];
      if (doc.isPrefilledPdfEnabled) {
        getPreFilledPdfUrl(doc).then((url) => {
          if (url) {
            setPdfUrl(url);
          }
        });
      } else {
        preview({
          variables: {
            input: {
              ...relatedFilterAttribute(
                signableDocuments[signableDocumentIndex],
              ),
              type: signableDocuments[signableDocumentIndex].type,
              language: languageAction.toString().toUpperCase(),
            },
          },
        });
      }
    }
    if (signableDocumentIndex === undefined) {
      setHtml('');
      setPdfUrl('');
    }
  }, [signableDocumentIndex, signableDocuments, preview, languageAction]);

  const viewNext = () => {
    if (signableDocumentIndex === undefined) return;
    if (signableDocumentIndex + 1 !== signableDocuments.length) {
      setSignableDocumentIndex(signableDocumentIndex + 1);
    }
  };

  const analyticsContext = {
    workflowStepTitle: options?.title,
    workflowId: workflowCompletion?.workflow?.id,
    workflowName: workflowCompletion?.workflow?.name,
    activeWorkflowCompletionId: workflowCompletion?.id,
    objectId: workflowCompletion?.objectId,
    objectType: workflowCompletion?.objectType,
  };

  return (
    <DocumentsVisual
      options={options}
      signableDocuments={signableDocuments}
      uploadableDocuments={uploadableDocuments}
      signAgreement={() => {
        scrollWorkflowDialog();
        if (signableDocumentIndex === undefined) return;
        if (options.requireSigning) {
          doSignDocument(signableDocuments[signableDocumentIndex]);
        }

        viewNext();
      }}
      downloadTemplatePDF={downloadTemplatePDF}
      doTheUpload={doTheUpload}
      doTheUploadForSignable={doTheUploadForSignable}
      canManuallyUploadFormAgreement={canManuallyUploadFormAgreement}
      onUploadSignedDocumentClick={() => {
        sendAnalytic(
          'workflows_documents_upload_signed_document_button_select',
          analyticsContext,
        );
      }}
      uploadInProgress={uploadUrlLoading || uploadPending || createFilePending}
      signFormAgreementPending={signFormAgreementPending}
      viewNext={viewNext}
      signAll={signAll}
      continueFunc={() => {
        sendAnalytic(
          ovAnalyticsEvents.workflowsDocumentsContinueButtonSelect,
          analyticsContext,
        );
        onNext();
      }}
      loading={
        incompleteAgreementsLoading || signFormAgreementPending || stepLoading
      }
      signableDocumentIndex={signableDocumentIndex}
      setSignableDocumentIndex={setSignableDocumentIndex}
      uploadableDocumentIndex={uploadableDocumentIndex}
      setUploadableDocumentIndex={setUploadableDocumentIndex}
      html={html}
      pdfUrl={pdfUrl}
      previewLoading={previewLoading || downloadUrlLoading}
      userJurisdiction={userJurisdiction}
      languageAction={languageAction}
      updateLanguageAction={setLanguageAction}
    />
  );
};

export default Documents;
